import type { SearchResultClickedHandler } from "@portal/hooks/useSearchAnalytics";
import { type FC, type RefObject, useEffect, useRef } from "react";
import { match } from "ts-pattern";
import { cn } from "../../lib/utils";
import { GlobalSearchResult } from "./GlobalSearchResult";
import {
	type AccountSearchEntity,
	ActiveFilterEnum,
	type BondRequestSearchEntity,
	type BondSearchEntity,
} from "./types";

export const getCategoryTitle = (category: Exclude<ActiveFilterEnum, ActiveFilterEnum.ALL>) =>
	match(category)
		.with(ActiveFilterEnum.ACCOUNTS, () => "Accounts")
		.with(ActiveFilterEnum.BONDS, () => "Bonds")
		.with(ActiveFilterEnum.REQUESTS, () => "Bond Requests")
		.exhaustive();

export type GlobalSearchResultsCategoryProps = (
	| {
			category: ActiveFilterEnum.ACCOUNTS;
			results: AccountSearchEntity[] | undefined;
	  }
	| {
			category: ActiveFilterEnum.REQUESTS;
			results: BondRequestSearchEntity[] | undefined;
	  }
	| {
			category: ActiveFilterEnum.BONDS;
			results: BondSearchEntity[] | undefined;
	  }
) & {
	searchTerm?: string;
	stickyTopOffset?: number;
	scrollContainerRef?: RefObject<HTMLDivElement | null>;
	onJumpTo?: () => void;
	/**
	 * When true (default), a non-sticky category owns its own vertical scroll and
	 * stretches to fill the available height. Set false when a parent container
	 * handles scrolling and the category should flow inline (e.g. the dashboard panel).
	 */
	selfScroll?: boolean;
	onResultClicked: SearchResultClickedHandler;
	/** Invoked on result click before navigation. Forwarded to each result; defaults to closing the global-search modal. */
	onNavigate?: () => void;
};

export const GlobalSearchResultsCategory: FC<GlobalSearchResultsCategoryProps> = ({
	category,
	results,
	searchTerm,
	stickyTopOffset,
	scrollContainerRef,
	onJumpTo,
	selfScroll = true,
	onResultClicked,
	onNavigate,
}) => {
	const isSticky = stickyTopOffset !== undefined;
	const watcherRef = useRef<HTMLDivElement>(null);
	const jumpToRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		const watcher = watcherRef.current;
		const jumpTo = jumpToRef.current;
		const root = scrollContainerRef?.current;
		if (!isSticky || !watcher || !jumpTo || !root) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				jumpTo.style.visibility = entry.isIntersecting ? "hidden" : "visible";
				jumpTo.style.pointerEvents = entry.isIntersecting ? "none" : "auto";
			},
			{ root, threshold: 0, rootMargin: "-1px 0px 0px 0px" },
		);
		observer.observe(watcher);
		return () => observer.disconnect();
	}, [isSticky]);

	const content = (
		<>
			<div
				data-category={category}
				className={cn(
					"flex items-center gap-1 text-xs text-secondary font-medium p-2 w-full",
					isSticky && "sticky bg-surface-neutral z-10",
				)}
				style={isSticky ? { top: stickyTopOffset } : undefined}
			>
				<div data-testid="global-search-category-title">{getCategoryTitle(category)}</div>
				{results && results.length > 0 && searchTerm && (
					<div data-testid="global-search-results-count" className="capitalize">
						{`(${results.length} ${results.length === 1 ? "RESULT" : "RESULTS"})`}
					</div>
				)}
				{isSticky && (
					<button
						ref={jumpToRef}
						type="button"
						className="ml-auto text-xs text-link hover:text-link-hover cursor-pointer"
						style={{ visibility: "hidden", pointerEvents: "none" }}
						onClick={(e) => {
							e.currentTarget.blur();
							onJumpTo?.();
						}}
					>
						Jump to
					</button>
				)}
			</div>
			<div
				className={cn(
					"flex-1 w-full gap-2 text-xs text-tertiary text-center pb-4",
					!isSticky && selfScroll && "overflow-y-auto max-h-[410px]",
				)}
			>
				{!searchTerm && (
					<span data-testid="global-search-no-term">Enter a search term to see results</span>
				)}
				{searchTerm &&
					(results && results.length === 0 ? (
						<span data-testid="global-search-no-results">No results for "{searchTerm}"</span>
					) : (
						<>
							{results?.map((result) => (
								<GlobalSearchResult
									key={result.entity.id}
									{...result}
									resultSource="search"
									onResultClicked={onResultClicked}
									onNavigate={onNavigate}
								/>
							))}
						</>
					))}
			</div>
		</>
	);

	return isSticky ? (
		<>
			<div
				ref={watcherRef}
				data-category-watcher={category}
				className="h-px w-full"
				aria-hidden="true"
			/>
			{content}
		</>
	) : (
		<div className={cn("flex flex-col items-start gap-2 w-full", selfScroll && "flex-1 h-full")}>
			{content}
		</div>
	);
};
